import { useState, useEffect, useCallback } from "react";
import { TrendingUp, BarChart3, FileText, Package, Image } from "lucide-react";
import adminApi from "@/lib/adminApi";

function StatCard({ label, value, icon: Icon, color = "text-[#CBA052]" }) {
  return (
    <div className="border border-white/[0.06] bg-white/[0.02] rounded p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={color} />
        <span className="text-[10px] text-white/40 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await adminApi.get("/api/admin/analytics");
      setData(res.data);
    } catch (err) { console.error("[AdminAnalytics] fetch failed:", err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="border border-white/[0.08] bg-[#060606] p-8 text-center text-xs text-white/30">Loading analytics...</div>;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Quotes" value={data?.total_quotes || 0} icon={FileText} color="text-green-400" />
        <StatCard label="Products" value={data?.total_products || 0} icon={Package} />
        <StatCard label="Media Files" value={data?.total_media || 0} icon={Image} />
      </div>

      {/* Quotes by Type */}
      <div className="border border-white/[0.08] bg-[#060606]">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <BarChart3 size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Quotes by Project Type</h3>
        </div>
        <div className="p-5">
          {!data?.quotes_by_type?.length ? (
            <p className="text-xs text-white/30 text-center py-4">No quote data yet.</p>
          ) : (
            <div className="space-y-2">
              {data.quotes_by_type.map((item) => {
                const maxCount = data.quotes_by_type[0]?.count || 1;
                const pct = Math.round((item.count / maxCount) * 100);
                return (
                  <div key={item.type} className="flex items-center gap-3">
                    <span className="text-xs text-white/60 w-32 shrink-0 truncate">{item.type}</span>
                    <div className="flex-1 h-5 bg-white/[0.03] rounded overflow-hidden">
                      <div className="h-full bg-[#CBA052]/30 rounded" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-bold text-white w-8 text-right">{item.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Daily Quotes (last 30 days) */}
      <div className="border border-white/[0.08] bg-[#060606]">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <TrendingUp size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Daily Quotes (Last 30 Days)</h3>
        </div>
        <div className="p-5">
          {!data?.quotes_daily?.length ? (
            <p className="text-xs text-white/30 text-center py-4">No daily data yet.</p>
          ) : (
            <div className="flex items-end gap-1 h-24">
              {data.quotes_daily.map((day) => {
                const maxCount = Math.max(...data.quotes_daily.map((d) => d.count), 1);
                const height = Math.max((day.count / maxCount) * 100, 4);
                return (
                  <div key={day.date} className="flex-1 group relative" title={`${day.date}: ${day.count} quotes`}>
                    <div className="bg-[#CBA052]/40 hover:bg-[#CBA052]/70 transition-colors rounded-t" style={{ height: `${height}%` }} />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-black border border-white/20 rounded px-2 py-1 text-[9px] text-white whitespace-nowrap z-10">
                      {day.date}: {day.count}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
